import { useState, useEffect, useMemo } from 'react';
import { userAPI, produksjonAPI } from '@/services/api';
import { type UseDraftBemanningReturn } from '@/hooks/useDraftBemanning';

export interface BemanningUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number?: string;
  talents: Array<{
    talent_id: number;
    talent_navn: string;
    talent_kategori: string;
    erfaringsnivaa: string;
    sertifisert: boolean;
  }>;
  allokertTalenter?: string[];
}

interface UseDraftBemanningDrawerProps {
  open: boolean;
  produksjonId: number;
  selectedTalent: {
    talent_navn: string;
    talent_kategori: string;
    antall: number;
    antallFylt: number;
  } | null;
  draft: UseDraftBemanningReturn;
}

export const useDraftBemanningDrawer = ({
  open,
  produksjonId,
  selectedTalent,
  draft,
}: UseDraftBemanningDrawerProps) => {
  const [users, setUsers] = useState<BemanningUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<Set<number>>(new Set());
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const fetchUsers = async () => {
    if (!selectedTalent) return;

    setLoading(true);
    setError(null);
    try {
      const [usersData, bemanningData] = await Promise.all([
        userAPI.getAllWithTalents(),
        produksjonAPI.getBemanning(produksjonId),
      ]);
      
      let filteredUsers = usersData.users || [];

      if (!showAllUsers) {
        filteredUsers = filteredUsers.filter(user => 
          user.talents && user.talents.some((t: { talent_navn: string }) => t.talent_navn === selectedTalent.talent_navn)
        );
      }

      const usersWithAllokering = filteredUsers.map(user => {
        if (showAllUsers) {
          const allokertTalenter = bemanningData.bemanning
            .filter(b => b.person_id === user.id)
            .map(b => b.talent_navn);
          return {
            ...user,
            allokertTalenter: allokertTalenter.length > 0 ? allokertTalenter : undefined
          };
        } else {
          const existing = bemanningData.bemanning.find(
            b => b.person_id === user.id && b.talent_navn === selectedTalent.talent_navn
          );
          return {
            ...user,
            allokertTalenter: existing ? [existing.talent_navn] : undefined
          };
        }
      });

      usersWithAllokering.sort((a, b) => {
        const lastNameCompare = a.last_name.localeCompare(b.last_name, 'no');
        if (lastNameCompare !== 0) return lastNameCompare;
        return a.first_name.localeCompare(b.first_name, 'no');
      });

      setUsers(usersWithAllokering);
    } catch (err: any) {
      console.error('Feil ved henting av brukere:', err);
      setError('Kunne ikke hente brukere');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && selectedTalent) {
      fetchUsers();
      setSelectedUsers(new Set());
      setSearchQuery('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedTalent?.talent_navn, showAllUsers]);

  useEffect(() => {
    if (open && selectedTalent && !draft.isDraftMode) {
      draft.enterDraftMode(produksjonId, selectedTalent, users);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedTalent, users]);

  const toggleUserSelection = (userId: number, event?: React.MouseEvent) => {
    event?.stopPropagation();
    setSelectedUsers(prev => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  const handleAddToDraft = async () => {
    if (!selectedTalent || selectedUsers.size === 0) return;

    try {
      const selectedUserObjects = users.filter(user => selectedUsers.has(user.id));
      selectedUserObjects
        .filter(user =>
          // Ikke allerede lagt til i draft for akkurat dette talentet
          !draft.pendingChanges.added.some(
            p => p.id === user.id && (p as any).talent_navn === selectedTalent.talent_navn
          ) &&
          // Ikke allerede lagret i planen for akkurat dette talentet
          !user.allokertTalenter?.includes(selectedTalent.talent_navn)
        )
        .forEach(user => draft.addPerson({
          ...user,
          talent_navn: selectedTalent.talent_navn,
          talent_kategori_sti: selectedTalent.talent_kategori,
          status: 'planlagt'
        } as any));

      setSelectedUsers(new Set());
      setError(null);
    } catch (err: any) {
      console.error('Feil ved draft-tilføyelse:', err);
      setError('Kunne ikke legge til i draft');
    }
  };

  const displayList = useMemo(() => {
    const removedIds = new Set(draft.pendingChanges.removed.map(p => p.id));
    const mapById = new Map<number, any>();
    users.forEach(u => { if (!removedIds.has(u.id)) mapById.set(u.id, u); });
    // Kun legg til added-oppføringer som tilhører gjeldende talent,
    // slik at Thomas lagt til oppgave A ikke dukker opp i oppgave B sin liste.
    draft.pendingChanges.added
      .filter((p: any) => p.talent_navn === selectedTalent?.talent_navn)
      .forEach((p: any) => { mapById.set(p.id, p); });
    const baseDisplayList = Array.from(mapById.values());

    if (!searchQuery.trim()) {
      return baseDisplayList;
    }

    const query = searchQuery.toLowerCase().trim();
    return baseDisplayList.filter((user: BemanningUser) => {
      const firstName = user.first_name?.toLowerCase() || '';
      const lastName = user.last_name?.toLowerCase() || '';
      const phoneNumber = user.phone_number?.toLowerCase() || '';
      
      return (
        firstName.includes(query) ||
        lastName.includes(query) ||
        phoneNumber.includes(query) ||
        `${firstName} ${lastName}`.includes(query)
      );
    });
  }, [users, draft.pendingChanges, searchQuery, selectedTalent?.talent_navn]);

  return {
    users,
    loading,
    error,
    selectedUsers,
    showAllUsers,
    searchQuery,
    displayList,
    setShowAllUsers,
    setSearchQuery,
    toggleUserSelection,
    handleAddToDraft,
  };
};

